import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiChevronLeft, FiChevronRight, FiUser, FiMapPin, FiCalendar, FiClock } from 'react-icons/fi';
import { Badge, Modal } from './ui';

/** Local calendar YYYY-MM-DD (avoid toISOString shifting the day in Asia/HK etc.) */
function formatLocalDate(dateInput) {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function refId(ref) {
    if (ref == null) return '';
    return String(ref._id != null ? ref._id : ref);
}

/** Normalize "9:00", "09:00:00" → "09:00" so grid slots and API values match */
function normalizeTimeSlot(raw) {
    const s = String(raw ?? '').trim();
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return '';
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    if (Number.isNaN(h) || Number.isNaN(min)) return '';
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function bookingTimeHour(raw) {
    const norm = normalizeTimeSlot(raw);
    if (!norm) return NaN;
    return parseInt(norm.split(':')[0], 10);
}

/** Equipment booking day: date-only API strings stay literal; ISO instants use local calendar */
function equipmentBookingDateKey(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') {
        const t = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    }
    return formatLocalDate(value);
}

function normStatus(s) {
    return String(s || '').trim().toLowerCase();
}

/** Row id from Mongo / API (some payloads use `id` instead of `_id`) */
function resourceRowId(resource) {
    if (resource == null) return '';
    return String(resource._id != null ? resource._id : resource.id ?? '');
}

/** Calendar day at local noon — stable weekday / date matching vs UTC midnight shifts */
function dateAtLocalNoon(d) {
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x;
}

function displayDetailValue(v) {
    if (v == null || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
}

function DetailField({ label, value }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-2 py-2 border-b border-gray-100 dark:border-gray-700 text-sm">
            <dt className="font-medium text-primary-600 dark:text-gray-400">{label}</dt>
            <dd className="sm:col-span-2 text-primary-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                {displayDetailValue(value)}
            </dd>
        </div>
    );
}

function formatDetailDate(value) {
    if (value == null || value === '') return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

const MasterScheduleCalendar = ({ type = 'equipment' }) => {
    const [currentDate, setCurrentDate] = useState(() => dateAtLocalNoon(new Date()));
    const [resources, setResources] = useState([]); // Equipment or Venues
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [bookingsError, setBookingsError] = useState(null);
    const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
    const [onlyBorrowedEquipment, setOnlyBorrowedEquipment] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState(null);

    const TIME_SLOTS = [
        '09:00', '10:00', '11:00', '12:00', 
        '13:00', '14:00', '15:00', '16:00', '17:00'
    ];

    const fetchBookings = useCallback(async () => {
        setBookingsLoading(true);
        setBookingsError(null);
        try {
            const token = localStorage.getItem('token');
            const endpoint =
                type === 'equipment' ? '/api/equipment-bookings/all' : '/api/venue-bookings/all';

            const response = await fetch(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                let msg = `Could not load bookings (${response.status}).`;
                try {
                    const errBody = await response.json();
                    if (errBody?.message) msg = errBody.message;
                } catch {
                    /* ignore */
                }
                setBookings([]);
                setBookingsError(msg);
                return;
            }

            const data = await response.json();
            if (!Array.isArray(data)) {
                console.error('Master schedule: bookings response is not an array', data);
                setBookings([]);
                setBookingsError('Unexpected response from server. Expected a list of bookings.');
                return;
            }
            setBookings(data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setBookings([]);
            setBookingsError(error?.message || 'Network error loading bookings.');
        } finally {
            setBookingsLoading(false);
        }
    }, [type]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setBookingsError(null);
            try {
                const token = localStorage.getItem('token');
                const endpoint = type === 'equipment' ? '/api/equipment/all' : '/api/venues/all';

                const response = await fetch(endpoint, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!cancelled && response.ok) {
                    const data = await response.json();
                    setResources(Array.isArray(data) ? data : []);
                } else if (!cancelled) {
                    setResources([]);
                }
            } catch (error) {
                console.error(`Error fetching ${type}:`, error);
                if (!cancelled) setResources([]);
            } finally {
                if (!cancelled) setLoading(false);
            }

            if (!cancelled) await fetchBookings();
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [type, fetchBookings]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') fetchBookings();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [fetchBookings]);

    const bookingMatchesEquipment = (booking, resourceId) => {
        const idStr = String(resourceId);
        if (booking.equipment_id != null && refId(booking.equipment_id) === idStr) return true;
        const ids = booking.equipment_ids || [];
        return ids.some((id) => refId(id) === idStr);
    };

    const datesForView = useMemo(() => {
        if (viewMode === 'day') {
            return [dateAtLocalNoon(currentDate)];
        }
        const dates = [];
        const start = dateAtLocalNoon(currentDate);
        for (let i = 0; i < 7; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            dates.push(date);
        }
        return dates;
    }, [currentDate, viewMode]);

    const displayResources = useMemo(() => {
        if (type !== 'equipment' || !onlyBorrowedEquipment) {
            return resources;
        }
        const dateSet = new Set(datesForView.map((d) => formatLocalDate(d)));
        return resources.filter((resource) => {
            const rid = resourceRowId(resource);
            if (!rid) return false;
            return bookings.some((b) => {
                if (!['pending', 'confirmed'].includes(normStatus(b.status))) return false;
                if (!bookingMatchesEquipment(b, rid)) return false;
                const dStr = equipmentBookingDateKey(b.eqm_booking_date);
                return dateSet.has(dStr);
            });
        });
    }, [resources, bookings, onlyBorrowedEquipment, type, datesForView]);

    const formatDateHeader = (date) => {
        const options = { weekday: 'short', month: 'short', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    };

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const getBookingForCell = (resource, date, timeSlot) => {
        const dateStr = formatLocalDate(date);
        const resourceId = resourceRowId(resource);
        if (!resourceId) return undefined;

        return bookings.find((booking) => {
            const bookingDate =
                type === 'venue'
                    ? formatLocalDate(booking.booking_date)
                    : equipmentBookingDateKey(booking.eqm_booking_date);
            const bookingTimeRaw =
                type === 'venue'
                    ? booking.booking_time || ''
                    : booking.eqm_booking_time || '';
            const duration =
                type === 'venue'
                    ? booking.booking_duration || 1
                    : booking.eqm_booking_duration || 1;

            let resourceMatch = false;
            if (type === 'equipment') {
                resourceMatch = bookingMatchesEquipment(booking, resourceId);
            } else {
                resourceMatch = refId(booking.venue_id) === String(resourceId);
            }

            if (!resourceMatch || bookingDate !== dateStr) {
                return false;
            }

            const bookingStartHour = bookingTimeHour(bookingTimeRaw);
            const slotHour = parseInt(timeSlot.split(':')[0], 10);
            if (Number.isNaN(bookingStartHour) || Number.isNaN(slotHour)) return false;

            return slotHour >= bookingStartHour && slotHour < bookingStartHour + duration;
        });
    };

    const getCellClassName = (resource, date, timeSlot, booking) => {
        let className =
            'border-r border-b border-gray-300 dark:border-gray-600 min-h-[44px] sm:min-h-[48px] relative';

        if (booking) {
            className +=
                ' cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset';
            switch (normStatus(booking.status)) {
                case 'pending':
                    className += ' bg-yellow-100 dark:bg-yellow-900/30';
                    break;
                case 'confirmed':
                    className += ' bg-green-100 dark:bg-green-900/30';
                    break;
                case 'completed':
                    className += ' bg-gray-200 dark:bg-gray-700';
                    break;
                case 'cancelled':
                    className += ' bg-red-100 dark:bg-red-900/30';
                    break;
                default:
                    className += ' bg-blue-100 dark:bg-blue-900/30';
            }
        } else if (isWeekend(date)) {
            className += ' bg-gray-100 dark:bg-gray-800';
        } else {
            className += ' bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700';
        }

        return className;
    };

    const getBookingStartSlot = (booking, timeSlot) => {
        const raw =
            type === 'venue'
                ? booking.booking_time || ''
                : booking.eqm_booking_time || '';
        return normalizeTimeSlot(raw) === timeSlot;
    };

    const navigateDate = (direction) => {
        const base = dateAtLocalNoon(currentDate);
        const newDate = new Date(base);
        if (viewMode === 'day') {
            newDate.setDate(base.getDate() + direction);
        } else {
            newDate.setDate(base.getDate() + direction * 7);
        }
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(dateAtLocalNoon(new Date()));
    };

    const getResourceName = (resource) => {
        if (type === 'equipment') {
            return resource.eqm_name || 'Unknown Equipment';
        } else {
            return resource.venue_unit || 'Unknown Venue';
        }
    };

    const getResourceLocation = (resource) => {
        return resource.location || 'N/A';
    };

    const dates = datesForView;

    if (loading) {
        return (
            <div className="text-center py-12 text-primary-500 dark:text-gray-400">
                Loading schedule...
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-primary-200 dark:border-gray-600 overflow-hidden flex flex-col max-h-[calc(100vh-7.5rem)] md:max-h-[calc(100vh-6.5rem)] min-h-[280px]">
            {/* Header Controls */}
            <div className="p-3 sm:p-4 bg-primary-50 dark:bg-gray-700 border-b border-primary-200 dark:border-gray-600 shrink-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Date Navigation */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigateDate(-1)}
                            className="p-2 hover:bg-primary-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <FiChevronLeft className="w-5 h-5 text-primary-700 dark:text-white" />
                        </button>
                        
                        <div className="flex items-center gap-2 min-w-[200px] justify-center">
                            <FiCalendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            <h3 className="font-serif font-semibold text-primary-900 dark:text-white">
                                {viewMode === 'day' 
                                    ? formatDateHeader(currentDate)
                                    : `${formatDateHeader(dates[0])} - ${formatDateHeader(dates[dates.length - 1])}`
                                }
                            </h3>
                        </div>
                        
                        <button
                            onClick={() => navigateDate(1)}
                            className="p-2 hover:bg-primary-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <FiChevronRight className="w-5 h-5 text-primary-700 dark:text-white" />
                        </button>
                    </div>

                    {/* View Controls */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={goToToday}
                            className="px-3 py-1.5 text-sm bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 rounded-lg transition-colors text-primary-700 dark:text-white font-medium"
                        >
                            Today
                        </button>
                        
                        <div className="flex gap-1 bg-white dark:bg-gray-600 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('day')}
                                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                                    viewMode === 'day'
                                        ? 'bg-primary-500 text-white'
                                        : 'text-primary-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500'
                                }`}
                            >
                                Day
                            </button>
                            <button
                                onClick={() => setViewMode('week')}
                                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                                    viewMode === 'week'
                                        ? 'bg-primary-500 text-white'
                                        : 'text-primary-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500'
                                }`}
                            >
                                Week
                            </button>
                        </div>

                        {type === 'equipment' && (
                            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-primary-700 dark:text-gray-200 whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={onlyBorrowedEquipment}
                                    onChange={(e) => setOnlyBorrowedEquipment(e.target.checked)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span>Only equipment with bookings</span>
                            </label>
                        )}
                    </div>
                </div>
            </div>

            {bookingsError && (
                <div className="px-3 sm:px-4 py-2 bg-amber-50 dark:bg-amber-900/25 border-b border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0">
                    <span>{bookingsError}</span>
                    <button
                        type="button"
                        onClick={() => fetchBookings()}
                        className="text-xs font-medium underline decoration-amber-700 hover:no-underline whitespace-nowrap"
                    >
                        Retry loading bookings
                    </button>
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-50 dark:bg-gray-700 text-xs border-b border-gray-200 dark:border-gray-600 shrink-0">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-100 dark:bg-yellow-900/30 border border-gray-300 rounded"></div>
                    <span className="text-primary-600 dark:text-gray-300">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-100 dark:bg-green-900/30 border border-gray-300 rounded"></div>
                    <span className="text-primary-600 dark:text-gray-300">Confirmed</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 border border-gray-300 rounded"></div>
                    <span className="text-primary-600 dark:text-gray-300">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-100 dark:bg-gray-800 border border-gray-300 rounded"></div>
                    <span className="text-primary-600 dark:text-gray-300">Weekend</span>
                </div>
            </div>

            {/* Schedule Grid — fills remaining viewport; scroll inside only if many rows */}
            <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full min-w-[680px]">
                    <thead className="sticky top-0 z-10">
                        {/* Date headers (for week view) */}
                        {viewMode === 'week' && (
                            <tr className="bg-primary-100 dark:bg-gray-700">
                                <th className="border-r border-b border-gray-300 dark:border-gray-600 p-2 bg-primary-100 dark:bg-gray-700 sticky left-0 z-20">
                                    <div className="text-xs font-serif font-semibold text-primary-700 dark:text-gray-300">
                                        {type === 'equipment' ? 'Equipment' : 'Venue'}
                                    </div>
                                </th>
                                {dates.map((date, index) => (
                                    <th 
                                        key={index}
                                        colSpan={TIME_SLOTS.length}
                                        className={`border-r border-b border-gray-300 dark:border-gray-600 p-2 ${
                                            isWeekend(date) ? 'bg-gray-200 dark:bg-gray-800' : ''
                                        }`}
                                    >
                                        <div className="text-sm font-serif font-semibold text-primary-900 dark:text-white">
                                            {formatDateHeader(date)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        )}
                        
                        {/* Time slot headers */}
                        <tr className="bg-primary-50 dark:bg-gray-700">
                            <th className="border-r border-b border-gray-300 dark:border-gray-600 p-1.5 sm:p-2 bg-primary-100 dark:bg-gray-700 sticky left-0 z-20 w-[140px] sm:min-w-[160px] sm:w-[180px]">
                                <div className="text-xs font-serif font-semibold text-primary-700 dark:text-gray-300">
                                    {viewMode === 'day' ? `${type === 'equipment' ? 'Equipment' : 'Venue'} / Time` : 'Time'}
                                </div>
                            </th>
                            {dates.map((date, dateIndex) => (
                                TIME_SLOTS.map((timeSlot, slotIndex) => (
                                    <th 
                                        key={`${dateIndex}-${slotIndex}`}
                                        className={`border-r border-b border-gray-300 dark:border-gray-600 p-1 sm:p-2 min-w-[52px] sm:min-w-[64px] ${
                                            isWeekend(date) ? 'bg-gray-100 dark:bg-gray-800' : ''
                                        }`}
                                    >
                                        <div className="text-xs text-primary-700 dark:text-gray-300">
                                            {timeSlot}
                                        </div>
                                    </th>
                                ))
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayResources.length === 0 && type === 'equipment' && onlyBorrowedEquipment && (
                            <tr>
                                <td
                                    colSpan={1 + dates.length * TIME_SLOTS.length}
                                    className="border-b border-gray-300 dark:border-gray-600 p-8 text-center text-sm font-literary text-primary-600 dark:text-gray-400 space-y-2"
                                >
                                    <p>
                                        {bookingsError
                                            ? 'Fix the booking load error above, then retry.'
                                            : bookingsLoading
                                              ? 'Loading bookings…'
                                              : bookings.length === 0
                                                ? 'No bookings were returned from the server. Open Manage Bookings to confirm your data, refresh this page, or check that your account can access the booking API.'
                                                : `No equipment has a pending or confirmed booking on the visible ${viewMode === 'day' ? 'day' : 'days'} (${dates.map((d) => formatDateHeader(d)).join(viewMode === 'week' ? ' → ' : '')}). Try another date with the arrows, click Today, or turn off “Only equipment with bookings” to see all ${resources.length} rows.`}
                                    </p>
                                </td>
                            </tr>
                        )}
                        {displayResources.map((resource) => (
                            <tr key={resourceRowId(resource) || getResourceName(resource)} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                {/* Resource name */}
                                <td className="border-r border-b border-gray-300 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700 sticky left-0 z-10">
                                    <div className="text-sm font-medium text-primary-900 dark:text-white">
                                        {getResourceName(resource)}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-primary-500 dark:text-gray-400 mt-1">
                                        <FiMapPin className="w-3 h-3" />
                                        {getResourceLocation(resource)}
                                    </div>
                                </td>
                                
                                {/* Time slots */}
                                {dates.map((date, dateIndex) => (
                                    TIME_SLOTS.map((timeSlot, slotIndex) => {
                                        const booking = getBookingForCell(resource, date, timeSlot);
                                        const isStartSlot = booking && getBookingStartSlot(booking, timeSlot);
                                        
                                        return (
                                            <td 
                                                key={`${dateIndex}-${slotIndex}`}
                                                className={getCellClassName(resource, date, timeSlot, booking)}
                                                role={booking ? 'button' : undefined}
                                                tabIndex={booking ? 0 : undefined}
                                                title={booking ? 'Click for full booking and borrower details' : undefined}
                                                onClick={() => {
                                                    if (booking) {
                                                        setSelectedDetail({ booking, resource });
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (!booking) return;
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        setSelectedDetail({ booking, resource });
                                                    }
                                                }}
                                            >
                                                {isStartSlot && booking && (
                                                    <div className="p-1 text-xs pointer-events-none">
                                                        <div className="flex items-center gap-1 mb-1">
                                                            <FiUser className="w-3 h-3" />
                                                            <span className="font-semibold truncate">
                                                                {booking.user?.First_Name} {booking.user?.Last_Name}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                                                            <FiClock className="w-3 h-3" />
                                                            {(type === 'venue'
                                                                ? booking.booking_duration
                                                                : booking.eqm_booking_duration) || 1}
                                                            h
                                                        </div>
                                                        <Badge 
                                                            variant={
                                                                normStatus(booking.status) === 'confirmed' ? 'success' :
                                                                normStatus(booking.status) === 'pending' ? 'warning' :
                                                                normStatus(booking.status) === 'completed' ? 'neutral' :
                                                                'error'
                                                            }
                                                            size="sm"
                                                            className="mt-1 text-[10px]"
                                                        >
                                                            {booking.status}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            <div className="p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 text-xs text-primary-600 dark:text-gray-400 shrink-0">
                Showing {displayResources.length}
                {onlyBorrowedEquipment && type === 'equipment'
                    ? ` of ${resources.length} equipment with bookings`
                    : ` ${type === 'equipment' ? 'equipment items' : 'venues'}`}{' '}
                × {dates.length} day(s) × {TIME_SLOTS.length} time slots
                {type === 'equipment' && (
                    <span className="block sm:inline sm:ml-2 text-primary-500 dark:text-gray-500">
                        · {bookingsLoading ? 'Loading bookings…' : `${bookings.length} booking record(s) loaded`}
                    </span>
                )}
            </div>

            <Modal
                isOpen={!!selectedDetail}
                onClose={() => setSelectedDetail(null)}
                title={
                    type === 'equipment'
                        ? 'Equipment booking — borrower & form details'
                        : 'Venue booking — borrower & form details'
                }
                size="3xl"
            >
                {selectedDetail && (
                    <div className="max-h-[min(70vh,640px)] overflow-y-auto -m-2 p-2 space-y-1">
                        {type === 'equipment' ? (
                            <>
                                <p className="text-xs text-primary-500 dark:text-gray-400 mb-3">
                                    Everything below is stored from the booking submission (and lab records).
                                </p>
                                <DetailField
                                    label="Resource (this row)"
                                    value={`${getResourceName(selectedDetail.resource)} — ${getResourceLocation(selectedDetail.resource)}`}
                                />
                                <DetailField label="Booking ID" value={selectedDetail.booking._id} />
                                <DetailField label="Status" value={selectedDetail.booking.status} />
                                <DetailField
                                    label="Scheduled date"
                                    value={formatDetailDate(selectedDetail.booking.eqm_booking_date)}
                                />
                                <DetailField label="Start time" value={selectedDetail.booking.eqm_booking_time} />
                                <DetailField
                                    label="Duration (hours)"
                                    value={selectedDetail.booking.eqm_booking_duration}
                                />
                                <DetailField label="Quantity (units)" value={selectedDetail.booking.quantity} />
                                <div className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-gray-300">
                                    Borrower & contact
                                </div>
                                <DetailField
                                    label="Name"
                                    value={
                                        `${selectedDetail.booking.user?.First_Name || ''} ${selectedDetail.booking.user?.Last_Name || ''}`.trim() ||
                                            selectedDetail.booking.user?.full_name ||
                                            undefined
                                    }
                                />
                                <DetailField
                                    label="Email"
                                    value={
                                        selectedDetail.booking.user?.Email_Address ||
                                        selectedDetail.booking.user?.email
                                    }
                                />
                                <DetailField label="User ID" value={selectedDetail.booking.user_id} />
                                <DetailField label="Phone" value={selectedDetail.booking.phone_number} />
                                <div className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-gray-300">
                                    Booking form
                                </div>
                                <DetailField label="Affiliation" value={selectedDetail.booking.affiliation} />
                                <DetailField label="Course code" value={selectedDetail.booking.course_code} />
                                <DetailField label="Account code" value={selectedDetail.booking.account_code} />
                                <DetailField label="Booking nature" value={selectedDetail.booking.booking_nature} />
                                <DetailField label="Remarks" value={selectedDetail.booking.remarks} />
                                <DetailField
                                    label="Event / funding source"
                                    value={selectedDetail.booking.event_funding_source}
                                />
                                <div className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-gray-300">
                                    Equipment snapshot (on record)
                                </div>
                                <DetailField label="Equipment name" value={selectedDetail.booking.equipment_name} />
                                <DetailField
                                    label="Category"
                                    value={selectedDetail.booking.equipment_category}
                                />
                                <DetailField label="Type" value={selectedDetail.booking.equipment_type} />
                            </>
                        ) : (
                            <>
                                <p className="text-xs text-primary-500 dark:text-gray-400 mb-3">
                                    Everything below is stored from the venue booking submission.
                                </p>
                                <DetailField
                                    label="Venue (this row)"
                                    value={`${getResourceName(selectedDetail.resource)} — ${getResourceLocation(selectedDetail.resource)}`}
                                />
                                <DetailField label="Booking ID" value={selectedDetail.booking._id} />
                                <DetailField label="Status" value={selectedDetail.booking.status} />
                                <DetailField
                                    label="Scheduled date"
                                    value={formatDetailDate(selectedDetail.booking.booking_date)}
                                />
                                <DetailField label="Start time" value={selectedDetail.booking.booking_time} />
                                <DetailField
                                    label="Duration (hours)"
                                    value={selectedDetail.booking.booking_duration}
                                />
                                <DetailField label="Quantity" value={selectedDetail.booking.quantity} />
                                <DetailField label="Description" value={selectedDetail.booking.booking_desc} />
                                <DetailField
                                    label="Total pay"
                                    value={
                                        selectedDetail.booking.total_pay != null
                                            ? selectedDetail.booking.total_pay
                                            : undefined
                                    }
                                />
                                <DetailField
                                    label="Reserve small room"
                                    value={selectedDetail.booking.reserve_small_room}
                                />
                                <DetailField
                                    label="Reserve large room"
                                    value={selectedDetail.booking.reserve_large_room}
                                />
                                <div className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-gray-300">
                                    Borrower & contact
                                </div>
                                <DetailField
                                    label="Name"
                                    value={
                                        `${selectedDetail.booking.user?.First_Name || ''} ${selectedDetail.booking.user?.Last_Name || ''}`.trim() ||
                                            selectedDetail.booking.user?.full_name ||
                                            undefined
                                    }
                                />
                                <DetailField
                                    label="Email"
                                    value={
                                        selectedDetail.booking.user?.Email_Address ||
                                        selectedDetail.booking.user?.email
                                    }
                                />
                                <DetailField label="User ID" value={selectedDetail.booking.user_id} />
                                <DetailField label="Phone" value={selectedDetail.booking.phone_number} />
                                <div className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-gray-300">
                                    Booking form
                                </div>
                                <DetailField label="Affiliation" value={selectedDetail.booking.affiliation} />
                                <DetailField label="Course code" value={selectedDetail.booking.course_code} />
                                <DetailField label="Account code" value={selectedDetail.booking.account_code} />
                                <DetailField label="Booking nature" value={selectedDetail.booking.booking_nature} />
                                <DetailField label="Remarks" value={selectedDetail.booking.remarks} />
                                <DetailField
                                    label="Event / funding source"
                                    value={selectedDetail.booking.event_funding_source}
                                />
                            </>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default MasterScheduleCalendar;
